/**
 * BUG-1977 — the one conversion from a `Money` to an integer count of its
 * currency's minor units (cents, whole yen), for the boundary that speaks
 * integers: a payment gateway's amount. The exponent is read off the currency,
 * never a literal ×100 (ADR 060 — Money Type § 2.1). BUG-1978 moved the last
 * `*_cents` column to Money and deleted the reverse conversion with it.
 */
import Decimal from 'decimal.js';

import { minorUnits } from './currency';
import type { Money } from './money';
import { isWholeMinorUnits } from './whole-minor-units';

/**
 * A `Money` as a whole number of its currency's minor units: CA$2,500.50 → 250050,
 * ¥5,000 → 5000.
 *
 * Refuses rather than rounds an amount finer than the minor unit: the caller
 * validates the step first (`minorUnitStepMessage`), so a fraction reaching here
 * is a bug upstream, and rounding it would store an amount nobody typed.
 *
 * @param money - The amount.
 * @returns The integer count of minor units.
 * @throws RangeError when the amount is not a whole number of minor units, or is
 *   past the safe-integer range.
 */
export function toMinorUnits(money: Money): number {
    if (!isWholeMinorUnits(money)) {
        throw new RangeError(`toMinorUnits: ${money.toDecimal().toString()} ${money.currency} is finer than its minor unit`);
    }
    const count = money.toDecimal().times(new Decimal(10).pow(minorUnits(money.currency)));
    if (count.abs().greaterThan(Number.MAX_SAFE_INTEGER)) {
        throw new RangeError(`toMinorUnits: ${count.toString()} minor units is past the safe-integer range`);
    }
    return count.toNumber();
}

